/**
 * laser-beam — 雷射光束。動詞:**自動鎖定、持續燒**(boss 2026-06-17 重新設計)。
 *
 * 玩法:武器自動放出一道**持續光束**,從砲口**瞄準最近的前方敵人**射出(指向性,會隨敵人
 * 移動而掃),沿線 halfHeight 內的敵人都被燒(低傷高頻)。深度來自「把敵人排到同一直線上,
 * 一道光束串燒一排」+ **過熱**機制:持續放會升溫,熱量見頂 → 過熱強制冷卻一小段(這段不放),
 * 降夠才恢復。進化(散彈/穿透)→ **多道光束分別鎖不同敵人**(boss 要的「進化後多條線」)。
 *
 * 跟軌道砲(railgun:橫向蓄力一發、瞬發貫穿)徹底不同:雷射是青色、持續、會瞄準、燒接觸點。
 *
 * 純邏輯(熱量曲線 advanceHeat/heatRatio)在 types.ts,可單測;此檔只組裝,選目標/施傷/渲染
 * 留 scene(透過 ctx.fireLaserAimed)。鐵則:威力 = baseBulletDamage × 倍率 × 武器等級曲線,
 * **不讀修煉值**。
 */

import type { WeaponBehavior, WeaponTickContext, HeatState, HeatParams } from './types';
import {
  advanceHeat,
  heatRatio,
  weaponLevelDamageMult,
  applyRateMod,
  sizeRangeMult,
  blastRangeBonus,
  extraHitChains,
  pierceDamageFalloff,
  LANE_DAMAGE_FALLOFF,
} from './types';

export const LASER_BEAM_ID = 'laser-beam';
export const LASER_BEAM_BLURB = '持續雷射,上下移動把光束掃過不同排敵人;放久會過熱要走開散熱。';

/** 光束覆蓋的半高(px):playerY ± 此值內的敵人被掃到(一條較窄 lane,鼓勵移動瞄準)。 */
export const LASER_BEAM_HALF_HEIGHT = 22;
/**
 * 每「施傷 tick」的傷害倍率(boss 2026-06-14「雷射的加成更扯,不能打得又廣傷害又高」)。
 * 雷射是「掃射/廣命中」武器(一條 lane 同時掃多個敵人 + 額外 lane),其每秒傷害必須
 * 明顯低於單體高傷武器(railgun)。0.5→0.28(上一輪下修,保留不 revert)。
 * boss 2026-06-14 第二輪「算範圍×傷害」:0.28→0.30 微回。維持「最低單發/掃射廣」定位
 * (仍明顯低於 railgun 單體),但不至於低到掉出收斂帶下緣(過熱已另外壓 uptime)。
 */
export const LASER_DAMAGE_MULT = 0.30;
/**
 * 施傷節流間隔(ms,boss 2026-06-14 雷射下修):每這麼久結算一次傷害(不綁 fps)。
 * 80→120:拉長間隔 = 降每秒結算次數 = 進一步壓低雷射 DPS,維持「掃射廣但傷害低」定位。
 * 校準:讓雷射單體 DPS 明顯低於 railgun 單體高傷(掃射的代價 = 單發弱),見
 * tests/game/progression/weapon-dps-convergence.test.ts。
 */
export const LASER_TICK_INTERVAL_MS = 120;
/**
 * 光束半高封頂 (boss 2026-06-17「大白光帶蓋住畫面」)。sizeRangeMult=bulletSizeMult 無上限,
 * 巨彈疊滿(×2×1.6×1.8≈5.76)→ halfHeight 127 → 帶高 254px 蓋半個畫面。封在 40(帶高 80px)。
 */
export const LASER_MAX_HALF_HEIGHT = 40;

/** 過熱曲線(Lv1 基準)。等級高 → 散熱快、上限高(見 heatParamsForLevel)。 */
export const LASER_MAX_HEAT = 1;
export const LASER_HEAT_PER_SEC = 0.42;   // 約 2.4s 連放見頂
export const LASER_COOL_PER_SEC = 0.6;    // 過熱後約 1.4s 散到可恢復
export const LASER_RESUME_HEAT = 0.15;    // 降到 15% 才解除過熱(滯後)

/** 依武器等級調過熱曲線:每級散熱 +6%、升溫 -4%(撐更久、恢復更快),不讀修煉值。 */
export function heatParamsForLevel(level: number): HeatParams {
  const lv = Number.isFinite(level) && level >= 1 ? Math.floor(level) : 1;
  const bonus = lv - 1;
  return {
    maxHeat: LASER_MAX_HEAT,
    heatPerSec: LASER_HEAT_PER_SEC * Math.max(0.5, 1 - bonus * 0.04),
    coolPerSec: LASER_COOL_PER_SEC * (1 + bonus * 0.06),
    resumeHeat: LASER_RESUME_HEAT,
  };
}

/**
 * 「攻擊增速」詮釋(laser 版):effectiveAutoFireMult < 1 → 升溫更慢、散熱更快
 * (撐更久、過熱恢復更快 = 實質提高雷射 uptime)。把等級曲線的 heatParams 再乘上
 * 射速倍率:heatPerSec × mult(放更久才過熱)、coolPerSec ÷ mult(散更快)。
 * mult 已 clamp(下限 0.4),疊到上限也只會「升溫 ~0.4×、散熱 ~2.5×」,不會爆。
 */
export function moddedHeatParams(level: number, effAutoFireMult: number): HeatParams {
  const base = heatParamsForLevel(level);
  const m = Number.isFinite(effAutoFireMult) && effAutoFireMult > 0 ? effAutoFireMult : 1;
  return {
    maxHeat: base.maxHeat,
    heatPerSec: base.heatPerSec * m,
    coolPerSec: base.coolPerSec / m,
    resumeHeat: base.resumeHeat,
  };
}

export class LaserBeamBehavior implements WeaponBehavior {
  readonly id = LASER_BEAM_ID;
  private _heat: HeatState = { heat: 0, overheated: false };
  private _lastTickMs = -1;

  tick(ctx: WeaponTickContext): void {
    // 三選一詮釋 — laser 版(boss 2026-06-17 改為「自動鎖定光束」):
    //   攻擊增速 → 升溫慢/散熱快(moddedHeatParams)+ 施傷 tick 更密(tickInterval × mult)。
    //   散彈/多子彈 → 多道光束分別鎖不同敵人(boss「進化後多條線」,各道傷害衰減)。
    //   巨彈/爆裂 → 光束加粗(×bulletSizeMult + spreadBlastRadius 帶寬,沿線命中更寬)。
    //   穿透/彈跳 → 也加一道光束(再多鎖一個敵人,各自施傷)。
    const m = ctx.mods;
    const effAuto = m.effectiveAutoFireMult();
    const params = moddedHeatParams(ctx.weaponLevel, effAuto);
    // 武器永遠「想放」(全自動,無按鈕);實際能不能放由過熱決定。
    const res = advanceHeat(this._heat, ctx.dt, true, params);
    this._heat.heat = res.heat;
    this._heat.overheated = res.overheated;

    // 過熱條 UI(0..1 + 是否過熱中)。headless 缺 seam 時略過。
    ctx.setHeatRatio?.(heatRatio(res.heat, params.maxHeat), res.overheated);

    // 光束半高(= 沿瞄準線命中的上下/側向寬度):巨彈倍率 + 爆裂帶寬。
    // 封頂 LASER_MAX_HALF_HEIGHT,避免巨彈疊滿把光束變成蓋住畫面的大白帶(boss 2026-06-17)。
    const halfHeight = Math.min(
      LASER_MAX_HALF_HEIGHT,
      LASER_BEAM_HALF_HEIGHT * sizeRangeMult(m.bulletSizeMult) + blastRangeBonus(m.spreadBlastRadius, 0.5),
    );
    // 多道鎖定光束 (boss 2026-06-17「雷射進化後可以多條線」):
    //   散彈/多子彈 → 多道光束,第 0 道鎖最近敵人、第 1 道次近…(最直覺的「多條線」)
    //   穿透/彈跳 → 也加一道。各額外道吃傷害衰減(廣不滿傷,平衡同 railgun)。總數封頂 6。
    const spreadBeams = Math.min(3, m.effectiveSpreadBullets());
    const extraBeams = Math.min(
      5,
      extraHitChains(m.pierceCount, m.bounceCount + m.evoBounceBonus) + spreadBeams,
    );

    if (!res.canFire) return; // 過熱中 / 剛見頂這幀:不放,讓玩家被迫等散熱(光束消失)

    // 施傷節流:每 tickInterval 結算一次(視覺光束仍每幀畫,dmg=0 = 只畫不施傷)。
    // 攻擊增速也縮短施傷節流間隔(放更密 → DPS 提升,符合「更快」)。
    const tickIntervalMs = applyRateMod(LASER_TICK_INTERVAL_MS, effAuto);
    if (this._lastTickMs >= 0 && ctx.nowMs - this._lastTickMs < tickIntervalMs) {
      // 仍要畫這幀光束(連續感),但不重複施傷 → dmg=0(scene 對 0 傷只畫光束不施傷)。
      this._fireBeams(ctx, halfHeight, 0, extraBeams, m);
      return;
    }
    this._lastTickMs = ctx.nowMs;

    const dmg = Math.max(
      1,
      Math.round(ctx.baseBulletDamage() * LASER_DAMAGE_MULT * weaponLevelDamageMult(ctx.weaponLevel)),
    );
    this._fireBeams(ctx, halfHeight, dmg, extraBeams, m);
  }

  /**
   * 放主光束(鎖最近敵人)+ extraBeams 道(分別鎖次近敵人)。scene 依 beamIndex 取第 N 近
   * 的前方敵人當瞄準方向,沿線 halfHeight 內全敵人施傷 + 畫指向性光束。
   * 廣命中傷害衰減(boss 2026-06-14「不能打得又廣傷害又高」):主光束滿傷,第 N 道額外光束
   * 吃 dmg × LANE_DAMAGE_FALLOFF^N → 多道 = 更廣但後段更弱。dmg=0(節流幀)各道都 0。
   * 無 fireLaserAimed seam 的退化路徑(理論上 scene 一定帶):用一發直射子彈近似,仍走子彈池。
   */
  private _fireBeams(
    ctx: WeaponTickContext, halfHeight: number, dmg: number, extraBeams: number, m: WeaponTickContext['mods'],
  ): void {
    if (!ctx.fireLaserAimed) {
      if (dmg > 0) ctx.spawnBullet(dmg, 0, 1.2 * sizeRangeMult(m.bulletSizeMult));
      return;
    }
    ctx.fireLaserAimed(0, halfHeight, dmg);
    for (let i = 1; i <= extraBeams; i++) {
      const beamDmg = dmg > 0 ? pierceDamageFalloff(dmg, i, LANE_DAMAGE_FALLOFF) : 0;
      ctx.fireLaserAimed(i, halfHeight, beamDmg);
    }
  }

  reset(): void {
    this._heat = { heat: 0, overheated: false };
    this._lastTickMs = -1;
  }

  /** 測試/UI 用:目前熱量比(0..1)。 */
  heatRatio(): number {
    return heatRatio(this._heat.heat, LASER_MAX_HEAT);
  }

  /** 測試/UI 用:是否過熱冷卻中。 */
  overheated(): boolean {
    return this._heat.overheated;
  }
}
